import { createContext, useRef, useState } from "react";

import destinationService from "../services/destination.service";

export const DestinationContext = createContext();

export const normalizeDestinationOrdering = (ordering = []) => {
  if (typeof ordering === "string") {
    try {
      ordering = JSON.parse(ordering);
    } catch {
      return [];
    }
  }
  return Array.isArray(ordering) ? ordering : [];
};

const normalizeRecommendationOrders = (orders = []) =>
  (Array.isArray(orders) ? orders : []).map((order) => ({
    ...order,
    ordering: normalizeDestinationOrdering(order?.ordering),
  }));

export const mergeDestinationResponse = (current = {}, response = {}) => ({
  ...(current || {}),
  ...(response || {}),
  recommendations: Array.isArray(response?.recommendations)
    ? response.recommendations
    : current?.recommendations || [],
  recommendation_ordering: Array.isArray(response?.recommendation_ordering)
    ? normalizeRecommendationOrders(response.recommendation_ordering)
    : current?.recommendation_ordering || [],
});

export const selectDestinationRecommendations = (destination = {}, tabType) => {
  const order = (destination?.recommendation_ordering || []).find(
    (item) => String(item.type).trim() === String(tabType || "").trim(),
  );
  const recommendations = destination?.recommendations || [];

  return normalizeDestinationOrdering(order?.ordering)
    .map((id) =>
      recommendations.find(
        (recommendation) =>
          String(recommendation.recommendation_id) === String(id),
      ),
    )
    .filter(Boolean);
};

const findCreatedRecommendation = (current = {}, response = {}) => {
  if (response?.recommendation?.recommendation_id) {
    return response.recommendation;
  }

  const currentIds = new Set(
    (current?.recommendations || []).map((item) =>
      String(item.recommendation_id),
    ),
  );

  return (response?.recommendations || []).find(
    (item) =>
      item?.recommendation_id &&
      !currentIds.has(String(item.recommendation_id)),
  );
};

export const mergeCreatedDestinationRecommendation = (
  current = {},
  response = {},
  submittedRecommendation = {},
) => {
  const mergedDestination = mergeDestinationResponse(current, response);
  const savedRecommendation =
    findCreatedRecommendation(current, response) ||
    (response?.recommendation_id ? response : null);

  if (!savedRecommendation) return mergedDestination;

  return addDestinationRecommendation(mergedDestination, {
    ...submittedRecommendation,
    ...savedRecommendation,
  });
};

export const mergeUpdatedDestinationRecommendation = (
  current = {},
  response = {},
  submittedRecommendation = {},
) => {
  const submittedId = String(
    submittedRecommendation?.recommendation_id || "",
  );
  const responseRecommendation =
    response?.recommendation ||
    (response?.recommendation_id ? response : null) ||
    (response?.recommendations || []).find(
      (item) => String(item.recommendation_id) === submittedId,
    );
  const currentRecommendation = (current?.recommendations || []).find(
    (item) => String(item.recommendation_id) === submittedId,
  );
  const mergedDestination = mergeDestinationResponse(current, response);

  return upsertDestinationRecommendation(mergedDestination, {
    ...(currentRecommendation || {}),
    ...(responseRecommendation || {}),
    ...submittedRecommendation,
    attachments:
      responseRecommendation?.attachments ??
      submittedRecommendation?.attachments ??
      currentRecommendation?.attachments ??
      [],
  });
};

export const upsertDestinationRecommendation = (
  current = {},
  recommendation,
) => {
  if (!recommendation?.recommendation_id) return current || {};

  const recommendationId = String(recommendation.recommendation_id);
  const recommendations = current?.recommendations || [];
  const exists = recommendations.some(
    (item) => String(item.recommendation_id) === recommendationId,
  );

  return {
    ...(current || {}),
    recommendations: exists
      ? recommendations.map((item) =>
          String(item.recommendation_id) === recommendationId
            ? { ...item, ...recommendation }
            : item,
        )
      : [recommendation, ...recommendations],
  };
};

export const addDestinationRecommendation = (
  current = {},
  recommendation,
) => {
  const next = upsertDestinationRecommendation(current, recommendation);
  if (!recommendation?.recommendation_id || !recommendation?.tab_id) {
    return next;
  }

  const id = String(recommendation.recommendation_id);
  const tabId = String(recommendation.tab_id).trim();
  const orders = normalizeRecommendationOrders(
    next.recommendation_ordering || [],
  );
  const hasTabOrder = orders.some(
    (order) => String(order.type).trim() === tabId,
  );

  return {
    ...next,
    recommendation_ordering: hasTabOrder
      ? orders.map((order) =>
          String(order.type).trim() === tabId
            ? {
                ...order,
                ordering: [
                  recommendation.recommendation_id,
                  ...normalizeDestinationOrdering(order.ordering).filter(
                    (itemId) => String(itemId) !== id,
                  ),
                ],
              }
            : order,
        )
      : [
          {
            type: recommendation.tab_id,
            ordering: [recommendation.recommendation_id],
          },
          ...orders,
        ],
  };
};

export const removeDestinationRecommendation = (
  current = {},
  recommendationId,
) => {
  const id = String(recommendationId);

  return {
    ...(current || {}),
    recommendations: (current?.recommendations || []).filter(
      (item) => String(item.recommendation_id) !== id,
    ),
    recommendation_ordering: (current?.recommendation_ordering || []).map(
      (order) => ({
        ...order,
        ordering: normalizeDestinationOrdering(order?.ordering).filter(
          (itemId) => String(itemId) !== id,
        ),
      }),
    ),
  };
};

export const DestinationProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [destinationId, setDestinationId] = useState(0);
  const [tabId, setTabId] = useState(0);
  const [isNew, setIsNew] = useState(false);
  const [listDestinations, setListDestinations] = useState([]);
  const [destination, setDestination] = useState({});
  const [section, setSection] = useState(null);
  const [selectedId, setSelectedId] = useState(0);
  const [shared, setShared] = useState(null);

  const [deletedRecommendation, setDeletedRecommendation] = useState(null);
  const recommendationMutationRevision = useRef(0);

  let contextDestination = {
    loading,
    listDestinations,
    destination,
    setDestination,
    selectedId,
    setSelectedId,
    shared,
    setShared,
    destinationId,
    setDestinationId,
    tabId,
    setTabId,
    isNew,
    setIsNew,
    section,
    setSection,

    deletedRecommendation,
    setDeletedRecommendation,

    get: async () => {
      setLoading(true);
      try {
        const result = await destinationService.getListDestinations();
        if (result.data) {
          setListDestinations(result.data);
        }
      } catch (error) {
        throw error;
      }
      setLoading(false);
    },
    initSection: async () => {
      setSection({
        google_place_id: null,
        address: null,
        lat: null,
        lng: null,
        display_as: "event",
        name: null,
        tip: null,
        external_link: null,
        attachments: null,
        start_date: null,
        end_date: null,
        start_time: null,
        end_time: null,
      });
    },
    getSelectedDestination: async (destination_id) => {
      try {
        setLoading(true);
        const requestRevision = recommendationMutationRevision.current;
        const result = await destinationService.getDestination(destination_id);
        if (requestRevision === recommendationMutationRevision.current) {
          setDestination(result.data);
        }
        setLoading(false);
        return result.data;
      } catch (error) {
        throw error;
      }
    },
    createDestination: async (objDestination) => {
      setLoading(true);
      try {
        let result = await destinationService.createDestination(objDestination);
        setLoading(false);
        return result.data;
      } catch (error) {
        throw error;
      }
    },
    updateDestination: async (destination) => {
      await destinationService.updateDestination(destination);
      return "success";
    },
    deleteDestination: async (destination_id) => {
      await destinationService.deleteDestination(destination_id);
      return "success";
    },
    createRecommendation: async (objRecommendation) => {
      let result =
        await destinationService.createRecommendation(objRecommendation);
      if (result?.data) {
        recommendationMutationRevision.current += 1;
        // Merge the full response and explicitly upsert the newly returned
        // recommendation. This keeps it visible even when the response's
        // ordering collection is stale or arrives in a different ID format.
        setDestination((current) =>
          mergeCreatedDestinationRecommendation(
            current,
            result.data,
            objRecommendation,
          ),
        );
      }
      return result;
    },
    deleteRecommendation: async (recommendation_id, destination_id) => {
      let resp = await destinationService.deleteRecommendation(
        recommendation_id,
        destination_id,
      );

      if (!resp.error) {
        recommendationMutationRevision.current += 1;
        setDestination((current) => {
          return removeDestinationRecommendation(
            mergeDestinationResponse(current, resp.data),
            recommendation_id,
          );
        });
      }
      return resp;
    },
    updateRecommendation: async (recommendation) => {
      let result =
        await destinationService.updateRecommendation(recommendation);
      recommendationMutationRevision.current += 1;
      setDestination((current) =>
        mergeUpdatedDestinationRecommendation(
          current,
          result.data,
          recommendation,
        ),
      );
      return result;
    },
    updateRecommendationOrder: async (order) => {
      let result = await destinationService.updateRecommendationOrder(order);
      return result;
    },
  };

  return (
    <DestinationContext.Provider value={contextDestination}>
      {children}
    </DestinationContext.Provider>
  );
};
